import { useEffect, useRef, useState } from "react";

/**
 * The pre-submit hold countdown (tasks/diner-booking-ui.md tech decision: server `expiresAt` is the
 * SINGLE truth, §D.4). Remaining time is DERIVED from `expiresAtSec` on every tick — never accumulated
 * by an interval — so it stays correct after a phone tab sleeps and is re-derived immediately on
 * `visibilitychange`. Expiry fires `onExpire` exactly once (the caller refetches alternatives, R7),
 * without polling the anonymous, rate-limited API (ADR-0029).
 *
 * Accessibility (ADR-0023): `announce` carries the just-crossed threshold (60/30/10/0 s) so the caller
 * can voice it via `aria-live="polite"` at thresholds ONLY — never the per-second visible tick.
 */

const THRESHOLDS = [60, 30, 10, 0] as const;
/** Ascending scan order — the smallest crossed bucket is the "current" one to announce. */
const ASCENDING = [0, 10, 30, 60] as const;

export interface HoldCountdown {
  remainingSec: number;
  expired: boolean;
  /** The threshold (seconds) just crossed, for a polite announcement; null between thresholds. */
  announce: number | null;
}

const nowSec = (): number => Math.floor(Date.now() / 1000);

export function useHoldCountdown(
  expiresAtSec: number | null,
  onExpire?: () => void,
): HoldCountdown {
  const [now, setNow] = useState(nowSec);
  const [announce, setAnnounce] = useState<number | null>(null);
  const announced = useRef<Set<number>>(new Set());
  const firedExpire = useRef(false);
  const onExpireRef = useRef(onExpire);
  // Keep the latest callback in a ref via an effect (not a render-time write) — refs must not be
  // written during render.
  useEffect(() => {
    onExpireRef.current = onExpire;
  });

  // Reset the announce state synchronously when a NEW hold is placed — adjusted during render
  // (comparing against the last-seen expiresAt) rather than via an effect, so the reset itself isn't a
  // synchronous setState-in-effect.
  const [lastExpiresAtSec, setLastExpiresAtSec] = useState(expiresAtSec);
  if (expiresAtSec !== lastExpiresAtSec) {
    setLastExpiresAtSec(expiresAtSec);
    setAnnounce(null);
  }

  // Reset + drive the tick whenever a NEW hold is placed (new expiresAt).
  useEffect(() => {
    announced.current = new Set();
    firedExpire.current = false;
    if (expiresAtSec === null) return;
    const tick = () => setNow(nowSec());
    tick();
    const id = setInterval(tick, 1000);
    const onVisible = () => tick(); // re-derive after a tab sleep — no drift
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [expiresAtSec]);

  const remainingSec = expiresAtSec === null ? 0 : Math.max(0, expiresAtSec - now);
  const expired = expiresAtSec !== null && remainingSec <= 0;

  // Threshold announcement + one-shot expiry, derived from the current remaining.
  useEffect(() => {
    if (expiresAtSec === null) return;
    for (const th of ASCENDING) {
      // The smallest crossed, not-yet-announced bucket is the "current" one.
      if (remainingSec <= th && !announced.current.has(th)) {
        for (const t of THRESHOLDS) if (t >= th) announced.current.add(t);
        setAnnounce(th);
        break;
      }
    }
    if (expired && !firedExpire.current) {
      firedExpire.current = true;
      onExpireRef.current?.();
    }
  }, [remainingSec, expired, expiresAtSec]);

  return { remainingSec, expired, announce };
}
